import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/authContext";
import {
  Sprout,
  Church,
  Crown,
  HandHeart,
  BookOpen,
  ArrowRight,
  Loader2,
} from "lucide-react";

const PROFILES = [
  {
    value: "new_convert",
    icon: Sprout,
    title: "Novo convertido",
    subtitle: "Comecei agora minha caminhada com Cristo.",
  },
  {
    value: "member",
    icon: Church,
    title: "Membro de igreja",
    subtitle: "Faço parte de uma comunidade local.",
  },
  {
    value: "leader",
    icon: Crown,
    title: "Líder",
    subtitle: "Sirvo na liderança da minha igreja.",
  },
  {
    value: "pastor",
    icon: HandHeart,
    title: "Pastor",
    subtitle: "Pastoreio um rebanho.",
  },
  {
    value: "seminarian",
    icon: BookOpen,
    title: "Seminarista",
    subtitle: "Estudo Teologia / preparo-me para o ministério.",
  },
];

export default function Onboarding() {
  const { user, isAuthenticated, isChecking, updateProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/";

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Guards
  if (isChecking) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center section-padding"
        data-testid="onboarding-checking"
      >
        <p className="text-navy/55 text-sm uppercase tracking-widest">
          Carregando…
        </p>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/entrar?next=/bem-vindo" replace />;
  // Já fez onboarding antes → segue para o destino sem refazer.
  if (user?.spiritual_profile) {
    return <Navigate to={next} replace />;
  }

  const save = async (value) => {
    setSaving(true);
    try {
      await updateProfile({ spiritual_profile: value });
      toast.success("Bem-vindo(a)! Sua jornada começa aqui.");
      navigate(next, { replace: true });
    } catch (e) {
      toast.error("Não foi possível salvar agora. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="max-w-2xl mx-auto section-padding py-10"
      data-testid="onboarding-page"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
        Seja bem-vindo(a)
      </p>
      <h1 className="font-display text-4xl text-navy mt-2">
        Em qual jornada você está?
      </h1>
      <p className="text-navy/65 mt-3 text-sm sm:text-base leading-relaxed max-w-lg">
        Sua resposta personaliza os planos de leitura, o tom do auxiliar IA e o
        conteúdo da sua jornada. Você pode mudar isso a qualquer momento.
      </p>

      <div
        className="mt-8 grid sm:grid-cols-2 gap-3"
        data-testid="onboarding-options"
      >
        {PROFILES.map((p) => {
          const Icon = p.icon;
          const isSel = selected === p.value;
          return (
            <button
              key={p.value}
              type="button"
              disabled={saving}
              onClick={() => {
                setSelected(p.value);
                save(p.value);
              }}
              data-testid={`onboarding-option-${p.value}`}
              aria-pressed={isSel}
              className={`text-left rounded-2xl border p-5 transition-all flex items-start gap-4 group disabled:opacity-60 ${
                isSel
                  ? "border-gold bg-[hsl(var(--parchment))] shadow-sm"
                  : "border-border bg-white hover:border-gold/70 hover:shadow-sm"
              }`}
            >
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                  isSel ? "bg-gold/20" : "bg-[hsl(var(--parchment))]"
                }`}
              >
                <Icon className="w-5 h-5 text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-navy">{p.title}</p>
                <p className="text-sm text-navy/65 mt-0.5 leading-relaxed">
                  {p.subtitle}
                </p>
              </div>
              <ArrowRight
                className={`w-4 h-4 mt-1.5 ${
                  isSel ? "text-gold" : "text-navy/30 group-hover:text-gold"
                }`}
              />
            </button>
          );
        })}
      </div>

      {saving && (
        <div
          className="mt-6 flex items-center gap-2 text-sm text-navy/65"
          data-testid="onboarding-saving"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Salvando…
        </div>
      )}

      <button
        type="button"
        disabled={saving}
        onClick={() => save("member")}
        data-testid="onboarding-skip"
        className="mt-8 text-[11px] uppercase tracking-widest text-navy/55 hover:text-navy disabled:opacity-50"
      >
        Pular por enquanto
      </button>
    </div>
  );
}
